import { useMemo } from 'react';
import { InjectedFormProps, reduxForm } from 'redux-form';

import RadioGroup from 'dashboard/components/FormControls/RadioGroup';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import { Inquiry, InquiryStatus } from 'dashboard/models/inquiry';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import FormControlField from 'lib/interfaces/FormControlField';
import { isRequired, withMessage } from 'lib/utils/formValidation';

interface FormReviewInquiryProps {
  handleCancel: () => void;
  inquiry: Inquiry;
}

const FormReviewInquiry = ({
  handleCancel,
  handleSubmit,
  inquiry,
  pristine,
  submitting,
}: FormReviewInquiryProps & InjectedFormProps<{}, FormReviewInquiryProps>) => {
  const validate = useMemo(
    () => ({
      status: [withMessage('Please select a status', isRequired)],
    }),
    [],
  );

  return (
    <form onSubmit={handleSubmit}>
      <FormControlField
        label='Select an approval status:'
        component={RadioGroup}
        name='status'
        data-test='form-review-inquiry__status'
        disabled={submitting}
        validate={validate.status}
        radioLabelValues={[
          {
            label: 'Approved',
            value: InquiryStatus.Approved,
            disabled: inquiry.attributes.status === InquiryStatus.Approved,
          },
          {
            label: 'Declined',
            value: InquiryStatus.Declined,
            disabled: inquiry.attributes.status === InquiryStatus.Declined,
          },
        ]}
        isCompact={true}
      />

      <FormControlField
        label='Comment on this review (optional):'
        component={TextAreaField}
        name='comment'
        disabled={submitting}
      />

      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-review-inquiry__cancel'
          data-test='form-review-inquiry__cancel'
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-review-inquiry__submit'
          disabled={submitting || pristine}
          isPending={submitting}
        >
          Submit
        </Button>
      </HStack>
    </form>
  );
};

export const formName = 'reviewInquiry';

export default reduxForm<{}, FormReviewInquiryProps>({
  form: formName,
})(FormReviewInquiry);
